import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, api, type AgentKey, type ApiKey, type NewApiKey } from "../api/client";
import { generateEd25519Keypair } from "../lib/keygen";
import { fingerprint, parseEd25519PublicKeyPem } from "../lib/pem";

type KeyClass = "write" | "read_only";
type KeyEnv = "live" | "test";

type KeypairSource = "generate" | "byo";

type CreateState = {
  label: string;
  keyClass: KeyClass;
  env: KeyEnv;
  submitting: boolean;
  error: string | null;
  issued: NewApiKey | null;
  copied: boolean;
  confirmingClose: boolean;
  // PR 15-B registration fields.
  register: boolean;
  keypairSource: KeypairSource;
  pastedPublicKey: string;
  pemError: string | null;
  // Set only on the browser-generated path, after a successful create:
  // the PKCS8 PEM the customer must download. Held in memory until the
  // dialog closes, then discarded.
  generatedPrivateKeyPem: string | null;
  privateKeyDownloaded: boolean;
  // The agent public key PEM derived locally (generated or BYO) when
  // registering. Kept so the success screen can compute the fingerprint
  // client-side — the create response no longer echoes the registered key.
  // Null when the key is not registered.
  registeredPublicKeyPem: string | null;
  // Fingerprint of the registered key (sha256:...), computed client-side
  // for the success screen. Null when the key is not registered.
  fingerprint: string | null;
  fingerprintExpanded: boolean;
};

type RotateState = {
  key: ApiKey;
  submitting: boolean;
  error: string | null;
  issued: NewApiKey | null;
  copied: boolean;
  confirmingClose: boolean;
};

type RevokeState = {
  key: ApiKey;
  reason: string;
  submitting: boolean;
  error: string | null;
};

type InitialIssued = { api_key: string; read_only_api_key: string };

const LABEL_MAX = 100;
const REASON_MAX = 200;
// Fallback used only until the list response arrives, or when talking to an
// older dashboard-api that doesn't return key_limit_per_class yet. It is the
// LEGACY cap (10), not the new default of 20: during a rollout where this
// bundle is served against the old API, the server still enforces 10, so
// showing 10 keeps the count and the key_limit_reached error text correct. The
// effective cap (new default 20 or a per-account override) replaces it as soon
// as the response sets keyLimit.
const DEFAULT_KEY_LIMIT = 10;

export function ApiKeysPage(props: { initialIssuedKeys?: InitialIssued }) {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [keyLimit, setKeyLimit] = useState<number>(DEFAULT_KEY_LIMIT);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showRevokedWrite, setShowRevokedWrite] = useState(false);
  const [showRevokedReadOnly, setShowRevokedReadOnly] = useState(false);
  const [createState, setCreateState] = useState<CreateState | null>(null);
  const [rotateState, setRotateState] = useState<RotateState | null>(null);
  const [revokeState, setRevokeState] = useState<RevokeState | null>(null);
  const [initialIssued, setInitialIssued] = useState<InitialIssued | null>(
    props.initialIssuedKeys ?? null
  );
  const [initialCopied, setInitialCopied] = useState<{ write: boolean; read: boolean }>({
    write: false,
    read: false
  });
  const [initialAcknowledged, setInitialAcknowledged] = useState(false);

  // The include_revoked flag follows whichever section toggle is on. We
  // fetch with the union so flipping either toggle on doesn't require a
  // round trip if the other was already on.
  const includeRevoked = showRevokedWrite || showRevokedReadOnly;

  async function loadKeys(opts: { includeRevoked: boolean }) {
    try {
      const result = await api.listApiKeys({ includeRevoked: opts.includeRevoked });
      setKeys(result.keys);
      // Always (re)derive from this response: an omitted key_limit_per_class
      // means an older/rolled-back API that still enforces the legacy cap, so
      // fall back rather than leaving a stale value from an earlier response.
      setKeyLimit(result.key_limit_per_class ?? DEFAULT_KEY_LIMIT);
      setLoadError(null);
      return result.keys;
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Could not load API keys.");
      throw err;
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await api.listApiKeys({ includeRevoked });
        if (!cancelled) {
          setKeys(result.keys);
          // See loadKeys: derive from each response so a later omit (mixed /
          // rollback deploy) resets to the legacy fallback instead of keeping
          // a stale higher limit.
          setKeyLimit(result.key_limit_per_class ?? DEFAULT_KEY_LIMIT);
          // Clear any stale error from a previous failed fetch.
          // Without this, a fetch that fails once and later succeeds
          // (e.g. when includeRevoked toggles) keeps the error banner
          // up even though fresh data loaded correctly. Codex P2 #8.
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : "Could not load API keys.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [includeRevoked]);

  const { writeKeys, readOnlyKeys } = useMemo(() => {
    const w: ApiKey[] = [];
    const r: ApiKey[] = [];
    for (const k of keys ?? []) {
      if (k.key_class === "write") w.push(k);
      else r.push(k);
    }
    return { writeKeys: w, readOnlyKeys: r };
  }, [keys]);

  const activeWriteCount = writeKeys.filter((k) => !k.revoked_at).length;
  const activeReadOnlyCount = readOnlyKeys.filter((k) => !k.revoked_at).length;

  function openCreate() {
    setCreateState({
      label: "",
      keyClass: "write",
      env: "live",
      submitting: false,
      error: null,
      issued: null,
      copied: false,
      confirmingClose: false,
      register: false,
      keypairSource: "generate",
      pastedPublicKey: "",
      pemError: null,
      generatedPrivateKeyPem: null,
      privateKeyDownloaded: false,
      registeredPublicKeyPem: null,
      fingerprint: null,
      fingerprintExpanded: false
    });
  }

  async function submitCreate() {
    if (!createState) return;
    const label = createState.label.trim();
    if (label.length === 0 || label.length > LABEL_MAX) return;

    // Resolve the agent public key (and, for the generate path, the
    // private key to show once) before the network call. The private key
    // never leaves the browser.
    let agentPublicKeyPem: string | undefined;
    let generatedPrivateKeyPem: string | null = null;
    if (createState.register) {
      if (createState.keypairSource === "generate") {
        const kp = generateEd25519Keypair();
        agentPublicKeyPem = kp.publicKeyPem;
        generatedPrivateKeyPem = kp.privateKeyPem;
      } else {
        const parsed = parseEd25519PublicKeyPem(createState.pastedPublicKey);
        if (!parsed.ok) {
          setCreateState((s) => (s ? { ...s, pemError: parsed.reason } : s));
          return;
        }
        agentPublicKeyPem = parsed.pem; // canonical SPKI PEM
      }
    }

    setCreateState({ ...createState, submitting: true, error: null, pemError: null });
    try {
      const issued = await api.createApiKey({
        label,
        key_class: createState.keyClass,
        env: createState.env,
        agent_public_key: agentPublicKeyPem
      });
      // The create response no longer echoes the registered key, so compute
      // the fingerprint from the locally-derived PEM (generated or BYO).
      const fp = agentPublicKeyPem ? await fingerprint(agentPublicKeyPem) : null;
      setCreateState((s) =>
        s
          ? {
              ...s,
              submitting: false,
              issued,
              copied: false,
              error: null,
              generatedPrivateKeyPem,
              privateKeyDownloaded: false,
              registeredPublicKeyPem: agentPublicKeyPem ?? null,
              fingerprint: fp
            }
          : s
      );
      await loadKeys({ includeRevoked }).catch(() => undefined);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.code === "key_limit_reached"
            ? `You've reached the limit of ${keyLimit} active ${createState.keyClass === "write" ? "write" : "read-only"} keys. Revoke an existing key to create a new one.`
            : err.message
          : "Could not create key.";
      setCreateState((s) => (s ? { ...s, submitting: false, error: message } : s));
    }
  }

  function tryCloseCreate() {
    if (!createState) return;
    // Confirm if either the API key or the (browser-generated) private
    // key is still unsaved.
    const apiKeyUnsaved = !!createState.issued && !createState.copied;
    const privateKeyUnsaved =
      !!createState.generatedPrivateKeyPem && !createState.privateKeyDownloaded;
    if (apiKeyUnsaved || privateKeyUnsaved) {
      setCreateState({ ...createState, confirmingClose: true });
      return;
    }
    setCreateState(null);
  }

  function downloadPrivateKey() {
    if (!createState?.generatedPrivateKeyPem || !createState.issued) return;
    const issued = createState.issued;
    downloadPrivateKeyPem(createState.generatedPrivateKeyPem, issued.label ?? "agent", issued.id);
    setCreateState((s) => (s ? { ...s, privateKeyDownloaded: true } : s));
  }

  function startRotate(key: ApiKey) {
    setRotateState({
      key,
      submitting: false,
      error: null,
      issued: null,
      copied: false,
      confirmingClose: false
    });
  }

  async function submitRotate() {
    if (!rotateState) return;
    setRotateState({ ...rotateState, submitting: true, error: null });
    try {
      const issued = await api.rotateApiKey(rotateState.key.id);
      setRotateState((s) => (s ? { ...s, submitting: false, issued, copied: false } : s));
      await loadKeys({ includeRevoked }).catch(() => undefined);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not rotate key.";
      setRotateState((s) => (s ? { ...s, submitting: false, error: message } : s));
    }
  }

  function tryCloseRotate() {
    if (!rotateState) return;
    if (rotateState.issued && !rotateState.copied) {
      setRotateState({ ...rotateState, confirmingClose: true });
      return;
    }
    setRotateState(null);
  }

  function startRevoke(key: ApiKey) {
    setRevokeState({
      key,
      reason: "",
      submitting: false,
      error: null
    });
  }

  async function submitRevoke() {
    if (!revokeState) return;
    const reason = revokeState.reason.trim();
    setRevokeState({ ...revokeState, submitting: true, error: null });
    try {
      await api.revokeApiKey(revokeState.key.id, reason || undefined);
      setRevokeState(null);
      await loadKeys({ includeRevoked }).catch(() => undefined);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not revoke key.";
      setRevokeState((s) => (s ? { ...s, submitting: false, error: message } : s));
    }
  }

  function dismissInitialIssued() {
    setInitialIssued(null);
    setInitialAcknowledged(false);
  }

  async function copyValue(value: string, after: () => void) {
    try {
      await navigator.clipboard.writeText(value);
      after();
    } catch {
      // Clipboard API not available (insecure context, permissions). The
      // caller's "Copied" state stays false so the user notices and can
      // hand-copy the key before dismissing.
    }
  }

  function retryLoad() {
    setLoading(true);
    loadKeys({ includeRevoked })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }

  if (loadError && !keys) {
    return (
      <div className="panel">
        <h2 className="section-title">API keys</h2>
        <div className="error">{loadError}</div>
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="panel">
        <div className="section-header">
          <h2 className="section-title">API keys</h2>
          <button className="btn" onClick={openCreate} disabled={loading && !keys}>
            Create new key
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Issue a key per agent or environment. Treat write keys like passwords; read-only keys are
          safe for log lookups and exports. Keys are shown exactly once at creation time.
        </p>
      </div>

      {initialIssued ? (
        <div className="panel key-issued-panel">
          <h2 className="section-title">Save these keys now</h2>
          <p className="warning-text">
            <strong>You won't see this again.</strong> Sequesign never stores the full key; we only
            keep a hash. If you lose it, you'll need to rotate again.
          </p>
          {initialIssued.api_key ? (
            <KeyDisplay
              label="Write key"
              value={initialIssued.api_key}
              copied={initialCopied.write}
              onCopy={() =>
                copyValue(initialIssued.api_key, () =>
                  setInitialCopied((s) => ({ ...s, write: true }))
                )
              }
            />
          ) : null}
          {initialIssued.read_only_api_key ? (
            <KeyDisplay
              label="Read-only key"
              value={initialIssued.read_only_api_key}
              copied={initialCopied.read}
              onCopy={() =>
                copyValue(initialIssued.read_only_api_key, () =>
                  setInitialCopied((s) => ({ ...s, read: true }))
                )
              }
            />
          ) : null}
          <label className="acknowledge-row">
            <input
              type="checkbox"
              checked={initialAcknowledged}
              onChange={(e) => setInitialAcknowledged(e.target.checked)}
            />
            <span>I've saved these keys somewhere safe.</span>
          </label>
          <button className="btn" disabled={!initialAcknowledged} onClick={dismissInitialIssued}>
            Done
          </button>
        </div>
      ) : null}

      <KeySection
        title="Write keys"
        keys={writeKeys}
        activeCount={activeWriteCount}
        keyLimit={keyLimit}
        showRevoked={showRevokedWrite}
        onToggleRevoked={setShowRevokedWrite}
        loading={loading}
        emptyMessage="You don't have any write keys yet. Click 'Create new key' to issue one."
        onRotate={startRotate}
        onRevoke={startRevoke}
      />

      <KeySection
        title="Read-only keys"
        keys={readOnlyKeys}
        activeCount={activeReadOnlyCount}
        keyLimit={keyLimit}
        showRevoked={showRevokedReadOnly}
        onToggleRevoked={setShowRevokedReadOnly}
        loading={loading}
        emptyMessage="You don't have any read-only keys yet. Click 'Create new key' to issue one."
        onRotate={startRotate}
        onRevoke={startRevoke}
      />

      {loadError && keys ? (
        <div className="panel">
          <div className="error">{loadError}</div>
          <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
            Retry
          </button>
        </div>
      ) : null}

      {createState ? (
        <CreateModal
          state={createState}
          onChange={setCreateState}
          onSubmit={submitCreate}
          onClose={tryCloseCreate}
          onForceClose={() => setCreateState(null)}
          onCopy={(value) =>
            copyValue(value, () => setCreateState((s) => (s ? { ...s, copied: true } : s)))
          }
          onDownloadPrivateKey={downloadPrivateKey}
        />
      ) : null}

      {rotateState ? (
        <RotateModal
          state={rotateState}
          onChange={setRotateState}
          onSubmit={submitRotate}
          onClose={tryCloseRotate}
          onForceClose={() => setRotateState(null)}
          onCopy={(value) =>
            copyValue(value, () => setRotateState((s) => (s ? { ...s, copied: true } : s)))
          }
        />
      ) : null}

      {revokeState ? (
        <RevokeModal
          state={revokeState}
          onChange={setRevokeState}
          onSubmit={submitRevoke}
          onClose={() => setRevokeState(null)}
        />
      ) : null}
    </>
  );
}

function KeySection(props: {
  title: string;
  keys: ApiKey[];
  activeCount: number;
  keyLimit: number;
  showRevoked: boolean;
  onToggleRevoked: (next: boolean) => void;
  loading: boolean;
  emptyMessage: string;
  onRotate: (key: ApiKey) => void;
  onRevoke: (key: ApiKey) => void;
}) {
  const active = props.keys.filter((k) => !k.revoked_at);
  const revoked = props.keys.filter((k) => k.revoked_at);
  const visible = props.showRevoked ? [...active, ...revoked] : active;
  const isLoading = props.loading && props.keys.length === 0;

  return (
    <div className="panel">
      <div className="section-header">
        <h2 className="section-title">{props.title}</h2>
        <span className="section-meta">
          {props.activeCount} of {props.keyLimit} active
        </span>
      </div>
      <label className="toggle-row">
        <input
          type="checkbox"
          checked={props.showRevoked}
          onChange={(e) => props.onToggleRevoked(e.target.checked)}
        />
        <span>Show revoked keys</span>
      </label>
      {isLoading ? (
        <p className="muted">Loading API keys…</p>
      ) : visible.length === 0 ? (
        <p className="empty-state">{props.emptyMessage}</p>
      ) : (
        <div className="key-list">
          {visible.map((k) => (
            <KeyRow key={k.id} apiKey={k} onRotate={props.onRotate} onRevoke={props.onRevoke} />
          ))}
        </div>
      )}
    </div>
  );
}

function KeyRow(props: {
  apiKey: ApiKey;
  onRotate: (key: ApiKey) => void;
  onRevoke: (key: ApiKey) => void;
}) {
  const k = props.apiKey;
  const revoked = !!k.revoked_at;
  const label = k.label ?? "Untitled";
  // Agent keys are an identity for signing, so they only apply to write-class
  // keys — matching the create modal, which only offers the register toggle
  // for write keys. We also hide the panel on revoked keys (nothing to manage).
  const [agentKeysExpanded, setAgentKeysExpanded] = useState(false);
  // True while the panel holds a freshly generated agent private key the
  // customer hasn't downloaded yet. Collapsing the disclosure unmounts the
  // panel and would discard that only-in-memory key (the server-side
  // registration is already active), so we block collapse while it's pending —
  // mirroring the in-panel "Done" gate, but at the unmount boundary.
  const [agentKeyPending, setAgentKeyPending] = useState(false);
  const showAgentKeysPanel = k.key_class === "write" && !revoked;
  return (
    <div className={revoked ? "key-item revoked" : "key-item"}>
      <div className="key-item-main">
        <div className="key-item-header">
          <span className="key-item-label">{label}</span>
          <span
            className={k.env === "live" ? "badge badge-live" : "badge badge-test"}
            aria-label={`Environment: ${k.env}`}
          >
            {k.env}
          </span>
          {revoked ? <span className="badge badge-revoked">revoked</span> : null}
        </div>
        <div className="key-item-meta">
          <span className="key-item-prefix">{k.key_prefix}…</span>
          <span title={k.created_at}>Created {formatRelative(k.created_at)}</span>
          <span>
            {k.last_used_at ? (
              <span title={k.last_used_at}>Last used {formatRelative(k.last_used_at)}</span>
            ) : (
              "Never used"
            )}
          </span>
        </div>
        {showAgentKeysPanel ? (
          <div className="agent-keys-disclosure">
            <button
              type="button"
              className="agent-keys-toggle"
              aria-expanded={agentKeysExpanded}
              // Don't allow collapse (which unmounts the panel) while a freshly
              // generated private key is still pending download.
              disabled={agentKeysExpanded && agentKeyPending}
              onClick={() => setAgentKeysExpanded((v) => !v)}
            >
              {agentKeysExpanded ? "▾" : "▸"} Agent keys
            </button>
            {agentKeysExpanded && agentKeyPending ? (
              <p className="field-hint" style={{ marginTop: 4 }}>
                Download the new private key below before collapsing this section.
              </p>
            ) : null}
            {agentKeysExpanded ? (
              <AgentKeysPanel
                apiKeyId={k.id}
                apiKeyLabel={label}
                onPendingChange={setAgentKeyPending}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="key-item-actions">
        {revoked ? null : (
          <>
            <button className="btn btn-secondary" onClick={() => props.onRotate(k)}>
              Rotate
            </button>
            <button className="btn btn-secondary" onClick={() => props.onRevoke(k)}>
              Revoke
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Shared one-time download of a browser-generated PKCS8 private-key PEM. The
// server never sees the private key, so this is the only chance to save it.
function downloadPrivateKeyPem(privateKeyPem: string, labelHint: string, idHint: string) {
  const labelPart =
    (labelHint || "agent").replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "agent";
  const filename = `sequesign-private-key-${labelPart}-${idHint.slice(0, 8)}.pem`;
  const blob = new Blob([privateKeyPem], { type: "application/x-pem-file" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// Local UI state for the "Add agent key" sub-form inside AgentKeysPanel.
type AddAgentKeyState = {
  open: boolean;
  keypairSource: KeypairSource;
  pastedPublicKey: string;
  pemError: string | null;
  submitting: boolean;
  error: string | null;
  // After a successful browser-generate enroll, hold the PKCS8 PEM until the
  // customer downloads it; the server never sees it.
  generatedPrivateKeyPem: string | null;
  // The public key currently being enrolled for the generated keypair. Kept so
  // a retry after a transient failure re-enrolls the SAME key (idempotent
  // server-side) instead of generating a new one and orphaning this private key.
  pendingPublicKeyPem: string | null;
  generatedForId: string | null;
  privateKeyDownloaded: boolean;
};

const EMPTY_ADD_STATE: AddAgentKeyState = {
  open: false,
  keypairSource: "generate",
  pastedPublicKey: "",
  pemError: null,
  submitting: false,
  error: null,
  generatedPrivateKeyPem: null,
  pendingPublicKeyPem: null,
  generatedForId: null,
  privateKeyDownloaded: false
};

function AgentKeysPanel(props: {
  apiKeyId: string;
  apiKeyLabel: string;
  // Reports whether a freshly generated private key is still pending download,
  // so the parent can block collapsing (unmounting) this panel and losing it.
  onPendingChange: (pending: boolean) => void;
}) {
  const { apiKeyId, apiKeyLabel, onPendingChange } = props;
  const [agentKeys, setAgentKeys] = useState<AgentKey[] | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [add, setAdd] = useState<AddAgentKeyState>(EMPTY_ADD_STATE);
  // Fingerprint expansion + revoke confirmation, keyed by agent-key id.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [confirmingRevoke, setConfirmingRevoke] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Surface "generated key pending download" to the parent so it can block
  // collapse/unmount; clear the flag when this panel unmounts.
  // Pending whenever a generated private key is held and not yet downloaded —
  // including the error/uncertain case (enrollment may have committed but the
  // response was lost), where generatedForId is still null. Guards collapse so
  // the key can't be discarded before it's saved.
  const pendingDownload = !!(add.generatedPrivateKeyPem && !add.privateKeyDownloaded);
  useEffect(() => {
    onPendingChange(pendingDownload);
  }, [pendingDownload, onPendingChange]);
  useEffect(() => {
    return () => onPendingChange(false);
  }, [onPendingChange]);

  async function load() {
    setLoading(true);
    try {
      const result = await api.listAgentKeys(apiKeyId);
      setAgentKeys(result.agent_keys);
      setLimit(result.limit);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Could not load agent keys.");
    } finally {
      setLoading(false);
    }
  }

  // Lazy-load on mount; the panel is only rendered while expanded.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await api.listAgentKeys(apiKeyId);
        if (!cancelled) {
          setAgentKeys(result.agent_keys);
          setLimit(result.limit);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : "Could not load agent keys.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiKeyId]);

  const active = (agentKeys ?? []).filter((a) => !a.revoked_at);
  const atLimit = limit !== null && active.length >= limit;

  async function submitRevoke(id: string) {
    setRevoking(id);
    setRevokeError(null);
    try {
      await api.revokeAgentKey(apiKeyId, id);
      setConfirmingRevoke(null);
      await load();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.code === "agent_key_registration_not_found"
            ? "That agent key was already revoked or no longer exists."
            : err.message
          : "Could not revoke agent key.";
      setRevokeError(message);
    } finally {
      setRevoking(null);
    }
  }

  // Enroll one public key. hasGeneratedKey distinguishes the browser-generate
  // path (whose private key is already persisted in add state) from BYO. On
  // failure we PRESERVE the generated private key (spread ...s) — the server
  // may have committed the enrollment before the response was lost, so the key
  // stays downloadable and retryable rather than being discarded.
  async function enrollPublicKey(pem: string, hasGeneratedKey: boolean) {
    setAdd((s) => ({ ...s, submitting: true, error: null, pemError: null }));
    try {
      const enrolled = await api.enrollAgentKey(apiKeyId, pem);
      if (hasGeneratedKey) {
        setAdd((s) => ({ ...s, submitting: false, generatedForId: enrolled.id, error: null }));
      } else {
        setAdd(EMPTY_ADD_STATE);
      }
      await load();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.code === "agent_key_limit_reached"
            ? `This key already has the maximum of ${limit ?? "the allowed"} active agent keys. Revoke one to add another.`
            : err.code === "invalid_agent_public_key"
              ? "The server rejected this public key. It must be a valid Ed25519 SPKI public key."
              : err.message
          : "Could not add agent key.";
      setAdd((s) => ({ ...s, submitting: false, error: message }));
    }
  }

  async function submitAdd() {
    if (add.keypairSource === "generate") {
      const kp = generateEd25519Keypair();
      // Persist the private + public key into state BEFORE the network call, so
      // a dropped/timed-out response (after the server may have committed the
      // enrollment) can't lose the only copy of the private key.
      setAdd((s) => ({
        ...s,
        generatedPrivateKeyPem: kp.privateKeyPem,
        pendingPublicKeyPem: kp.publicKeyPem,
        generatedForId: null,
        privateKeyDownloaded: false
      }));
      await enrollPublicKey(kp.publicKeyPem, true);
    } else {
      const parsed = parseEd25519PublicKeyPem(add.pastedPublicKey);
      if (!parsed.ok) {
        setAdd((s) => ({ ...s, pemError: parsed.reason }));
        return;
      }
      await enrollPublicKey(parsed.pem, false);
    }
  }

  // Retry a generated-key enrollment with the SAME public key after a transient
  // failure. Re-enrolling an already-registered key is idempotent server-side.
  async function retryEnroll() {
    if (!add.pendingPublicKeyPem) return;
    await enrollPublicKey(add.pendingPublicKeyPem, true);
  }

  // The one-time download of a freshly generated agent private key. Rendered at
  // the TOP of the panel, OUTSIDE the loading/loadError/list conditional, so a
  // failed list reload after a successful enrollment can never hide it — losing
  // it would leave the customer with a registered key they can't sign with.
  const pendingKeyDisplay = add.generatedPrivateKeyPem ? (
    <div className="key-display" style={{ marginBottom: 12 }}>
      <p className="warning-text" style={{ marginTop: 0 }}>
        <strong>Save the private key now.</strong> Sequesign never sees it and won't show it again.
      </p>
      <button
        className="btn btn-secondary"
        onClick={() => {
          downloadPrivateKeyPem(
            add.generatedPrivateKeyPem!,
            apiKeyLabel,
            add.generatedForId ?? "pending"
          );
          setAdd((s) => ({ ...s, privateKeyDownloaded: true }));
        }}
      >
        {add.privateKeyDownloaded ? "Private key downloaded" : "Download private key (.pem)"}
      </button>
      {add.error ? (
        <div style={{ marginTop: 8 }}>
          <div className="error">{add.error}</div>
          <p className="field-hint" style={{ marginTop: 4 }}>
            Enrollment may not have completed. Save the private key above, then retry —
            re-registering the same key is safe.
          </p>
          <button
            className="btn btn-secondary"
            style={{ marginTop: 4 }}
            disabled={add.submitting}
            onClick={() => retryEnroll()}
          >
            {add.submitting ? "Retrying…" : "Retry enrollment"}
          </button>
        </div>
      ) : null}
      <div style={{ marginTop: 8 }}>
        <button
          className="btn btn-secondary"
          // The agent key is already enrolled on the server, but this is the
          // only in-memory copy of its private key. Don't let "Done" discard it
          // until it's been downloaded, or the customer is left with an active
          // registered key they can't sign with.
          disabled={!add.privateKeyDownloaded}
          onClick={() => setAdd(EMPTY_ADD_STATE)}
        >
          Done
        </button>
        {!add.privateKeyDownloaded ? (
          <p className="field-hint" style={{ marginTop: 4 }}>
            Download the private key to finish — it can't be recovered later.
          </p>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div className="agent-keys-panel">
      {pendingKeyDisplay}
      {loading && agentKeys === null ? (
        <p className="muted">Loading agent keys…</p>
      ) : loadError ? (
        <>
          <div className="error">{loadError}</div>
          <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => load()}>
            Retry
          </button>
        </>
      ) : (
        <>
          <div className="agent-keys-header">
            Agent keys — {active.length} of {limit ?? "?"} registered
          </div>
          {active.length === 0 ? (
            <p className="muted">No agent keys registered for this key.</p>
          ) : (
            <ul className="agent-key-list">
              {active.map((a) => {
                const isExpanded = !!expanded[a.id];
                const short = `${a.agent_key_fingerprint.slice(0, "sha256:".length + 16)}…`;
                return (
                  <li key={a.id} className="agent-key-item">
                    <div className="agent-key-item-main">
                      <button
                        type="button"
                        className="key-fingerprint"
                        title="Click to show the full fingerprint"
                        onClick={() => setExpanded((s) => ({ ...s, [a.id]: !s[a.id] }))}
                      >
                        <code className="key-value">
                          {isExpanded ? a.agent_key_fingerprint : short}
                        </code>
                      </button>
                      <span className="muted" title={a.registered_at}>
                        Registered {formatRelative(a.registered_at)}
                      </span>
                    </div>
                    <div className="agent-key-item-actions">
                      {confirmingRevoke === a.id ? (
                        <>
                          <span className="muted">Revoke this agent key?</span>
                          <button
                            className="btn btn-danger"
                            disabled={revoking === a.id}
                            onClick={() => submitRevoke(a.id)}
                          >
                            {revoking === a.id ? "Revoking…" : "Confirm revoke"}
                          </button>
                          <button
                            className="btn btn-secondary"
                            disabled={revoking === a.id}
                            onClick={() => {
                              setConfirmingRevoke(null);
                              setRevokeError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setConfirmingRevoke(a.id);
                            setRevokeError(null);
                          }}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {revokeError ? <div className="error">{revokeError}</div> : null}

          {add.generatedPrivateKeyPem ? null : add.open ? (
            <div className="agent-key-add-form" style={{ marginTop: 12 }}>
              <span className="label">Keypair source</span>
              <div className="modal-radio-group">
                <label>
                  <input
                    type="radio"
                    name={`agent-keypair-source-${apiKeyId}`}
                    value="generate"
                    checked={add.keypairSource === "generate"}
                    onChange={() =>
                      setAdd((s) => ({ ...s, keypairSource: "generate", pemError: null }))
                    }
                    disabled={add.submitting}
                  />
                  Generate keypair (recommended)
                </label>
                <label>
                  <input
                    type="radio"
                    name={`agent-keypair-source-${apiKeyId}`}
                    value="byo"
                    checked={add.keypairSource === "byo"}
                    onChange={() => setAdd((s) => ({ ...s, keypairSource: "byo", pemError: null }))}
                    disabled={add.submitting}
                  />
                  Bring your own public key (advanced)
                </label>
              </div>
              {add.keypairSource === "generate" ? (
                <span className="char-count">
                  Your browser generates an Ed25519 keypair. The private key is shown once for you
                  to download; it never reaches Sequesign.
                </span>
              ) : (
                <>
                  <textarea
                    className="input"
                    rows={5}
                    style={{ marginTop: 8, fontFamily: "monospace" }}
                    placeholder={"-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----"}
                    value={add.pastedPublicKey}
                    onChange={(e) =>
                      setAdd((s) => ({ ...s, pastedPublicKey: e.target.value, pemError: null }))
                    }
                    disabled={add.submitting}
                  />
                  {add.pemError ? <div className="error">{add.pemError}</div> : null}
                </>
              )}
              {add.error ? <div className="error">{add.error}</div> : null}
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <button
                  className="btn"
                  disabled={
                    add.submitting ||
                    atLimit ||
                    (add.keypairSource === "byo" && add.pastedPublicKey.trim().length === 0)
                  }
                  onClick={submitAdd}
                >
                  {add.submitting ? "Adding…" : "Add"}
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={add.submitting}
                  onClick={() => setAdd(EMPTY_ADD_STATE)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-secondary"
                disabled={atLimit}
                title={atLimit ? "This key is at its agent-key limit." : undefined}
                onClick={() => setAdd({ ...EMPTY_ADD_STATE, open: true })}
              >
                Add agent key
              </button>
              {atLimit ? (
                <span className="char-count" style={{ marginLeft: 8 }}>
                  At the limit of {limit} agent keys. Revoke one to add another.
                </span>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CreateModal(props: {
  state: CreateState;
  onChange: (next: CreateState) => void;
  onSubmit: () => void;
  onClose: () => void;
  onForceClose: () => void;
  onCopy: (value: string) => void;
  onDownloadPrivateKey: () => void;
}) {
  const s = props.state;
  const labelTrim = s.label.trim();
  const labelInvalid = labelTrim.length === 0 || labelTrim.length > LABEL_MAX;
  // Disable submit when bring-your-own is selected but no PEM is pasted.
  const byoMissing =
    s.register && s.keypairSource === "byo" && s.pastedPublicKey.trim().length === 0;

  if (s.confirmingClose && s.issued) {
    const apiKeyUnsaved = !s.copied;
    const privateKeyUnsaved = !!s.generatedPrivateKeyPem && !s.privateKeyDownloaded;
    const message =
      apiKeyUnsaved && privateKeyUnsaved
        ? "You haven't saved the API key and the private key. Closing this dialog will discard both. Continue?"
        : privateKeyUnsaved
          ? "You haven't downloaded the private key. Closing this dialog will discard it. Continue?"
          : "You haven't copied the key. Closing this dialog will discard it. Continue?";
    return (
      <ModalShell warning>
        <h2 className="section-title">Discard the key?</h2>
        <p className="warning-text">{message}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            className="btn btn-secondary"
            onClick={() => props.onChange({ ...s, confirmingClose: false })}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={() => props.onForceClose()}>
            Discard
          </button>
        </div>
      </ModalShell>
    );
  }

  if (s.issued) {
    const registered = s.registeredPublicKeyPem !== null;
    const hasPrivateKey = s.generatedPrivateKeyPem !== null;
    return (
      <ModalShell warning>
        <h2 className="section-title">Key created</h2>
        <p className="warning-text">
          {hasPrivateKey ? (
            <strong>
              Save the API key AND the private key. Neither will be shown again. Losing either means
              creating a new key.
            </strong>
          ) : (
            <strong>Save it now. This is the only time it's shown.</strong>
          )}
        </p>
        <KeyDisplay
          label={s.issued.label ? `Key (${s.issued.label})` : "Key"}
          value={s.issued.api_key}
          copied={s.copied}
          onCopy={() => props.onCopy(s.issued!.api_key)}
        />
        {registered ? (
          <div className="key-display">
            <div className="label">Registered agent key</div>
            {s.fingerprint ? (
              <button
                type="button"
                className="key-fingerprint"
                title="Click to show the full fingerprint"
                onClick={() =>
                  props.onChange({ ...s, fingerprintExpanded: !s.fingerprintExpanded })
                }
              >
                <code className="key-value">
                  {s.fingerprintExpanded
                    ? s.fingerprint
                    : `${s.fingerprint.slice(0, "sha256:".length + 16)}…`}
                </code>
              </button>
            ) : null}
            {hasPrivateKey ? (
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-secondary" onClick={props.onDownloadPrivateKey}>
                  {s.privateKeyDownloaded
                    ? "Private key downloaded"
                    : "Download private key (.pem)"}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn" onClick={props.onClose}>
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell>
      <h2 className="section-title">New API key</h2>
      <div className="field">
        <label className="label" htmlFor="api-key-label">
          Label
        </label>
        <input
          id="api-key-label"
          className="input"
          autoFocus
          value={s.label}
          maxLength={LABEL_MAX}
          placeholder="e.g. Production agent"
          onChange={(e) => props.onChange({ ...s, label: e.target.value })}
          disabled={s.submitting}
        />
        <span className="char-count">
          {s.label.length}/{LABEL_MAX}
        </span>
      </div>
      <div className="field">
        <span className="label">Class</span>
        <div className="modal-radio-group">
          <label>
            <input
              type="radio"
              name="key-class"
              value="write"
              checked={s.keyClass === "write"}
              onChange={() => props.onChange({ ...s, keyClass: "write" })}
              disabled={s.submitting}
            />
            Write
          </label>
          <label>
            <input
              type="radio"
              name="key-class"
              value="read_only"
              checked={s.keyClass === "read_only"}
              onChange={() => props.onChange({ ...s, keyClass: "read_only" })}
              disabled={s.submitting}
            />
            Read-only
          </label>
        </div>
      </div>
      <div className="field">
        <span className="label">Environment</span>
        <div className="modal-radio-group">
          <label>
            <input
              type="radio"
              name="key-env"
              value="live"
              checked={s.env === "live"}
              onChange={() => props.onChange({ ...s, env: "live" })}
              disabled={s.submitting}
            />
            Live
          </label>
          <label>
            <input
              type="radio"
              name="key-env"
              value="test"
              checked={s.env === "test"}
              onChange={() => props.onChange({ ...s, env: "test" })}
              disabled={s.submitting}
            />
            Test
          </label>
        </div>
        <p className="field-hint">
          The environment is a label, not a sandbox. A test key gives you a separate identifier to
          use for testing, but it signs against the same witness and is billed identically to a live
          key — there is no free or discounted test usage.
        </p>
      </div>
      <div className="field">
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={s.register}
            onChange={(e) => props.onChange({ ...s, register: e.target.checked, pemError: null })}
            disabled={s.submitting}
          />
          <span>Register this key with an agent public key</span>
        </label>
        <span className="char-count">
          A registered agent key stamps a durable agent identity onto receipts this key finalizes;
          the broker accepts any agent key registered to the API key. Optional — you can register
          this initial one now and add or revoke more later from the key's "Agent keys" panel.
        </span>
      </div>
      {s.register ? (
        <div className="field">
          <span className="label">Keypair source</span>
          <div className="modal-radio-group">
            <label>
              <input
                type="radio"
                name="keypair-source"
                value="generate"
                checked={s.keypairSource === "generate"}
                onChange={() => props.onChange({ ...s, keypairSource: "generate", pemError: null })}
                disabled={s.submitting}
              />
              Generate keypair (recommended)
            </label>
            <label>
              <input
                type="radio"
                name="keypair-source"
                value="byo"
                checked={s.keypairSource === "byo"}
                onChange={() => props.onChange({ ...s, keypairSource: "byo", pemError: null })}
                disabled={s.submitting}
              />
              Bring your own public key (advanced)
            </label>
          </div>
          {s.keypairSource === "generate" ? (
            <span className="char-count">
              Your browser generates an Ed25519 keypair. The private key is shown once for you to
              download; it never reaches Sequesign.
            </span>
          ) : (
            <>
              <textarea
                className="input"
                rows={5}
                style={{ marginTop: 8, fontFamily: "monospace" }}
                placeholder={"-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----"}
                value={s.pastedPublicKey}
                onChange={(e) =>
                  props.onChange({ ...s, pastedPublicKey: e.target.value, pemError: null })
                }
                disabled={s.submitting}
              />
              {s.pemError ? <div className="error">{s.pemError}</div> : null}
            </>
          )}
        </div>
      ) : null}
      {s.error ? <div className="error">{s.error}</div> : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={props.onClose} disabled={s.submitting}>
          Cancel
        </button>
        <button
          className="btn"
          disabled={s.submitting || labelInvalid || byoMissing}
          onClick={props.onSubmit}
        >
          {s.submitting ? "Creating…" : "Create"}
        </button>
      </div>
    </ModalShell>
  );
}

function RotateModal(props: {
  state: RotateState;
  onChange: (next: RotateState) => void;
  onSubmit: () => void;
  onClose: () => void;
  onForceClose: () => void;
  onCopy: (value: string) => void;
}) {
  const s = props.state;
  const label = s.key.label ?? "Untitled";

  if (s.confirmingClose && s.issued) {
    return (
      <ModalShell warning>
        <h2 className="section-title">Discard the key?</h2>
        <p className="warning-text">
          You haven't copied the key. Closing this dialog will discard it. Continue?
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            className="btn btn-secondary"
            onClick={() => props.onChange({ ...s, confirmingClose: false })}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={() => props.onForceClose()}>
            Discard
          </button>
        </div>
      </ModalShell>
    );
  }

  if (s.issued) {
    return (
      <ModalShell warning>
        <h2 className="section-title">Key rotated</h2>
        <p className="warning-text">
          <strong>Save it now. This is the only time it's shown.</strong> The previous key has
          stopped working.
        </p>
        <KeyDisplay
          label={s.issued.label ? `Key (${s.issued.label})` : "Key"}
          value={s.issued.api_key}
          copied={s.copied}
          onCopy={() => props.onCopy(s.issued!.api_key)}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn" onClick={props.onClose}>
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell>
      <h2 className="section-title">Rotate {label}?</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        The current key will stop working immediately; the new key will be shown only once.
      </p>
      <p className="warning-text" style={{ marginTop: 0 }}>
        Rotation creates a new key with an empty agent-key registry; the agent keys registered on
        this key do not carry over. After rotating, register agent keys on the new key from its
        "Agent keys" panel.
      </p>
      {s.error ? <div className="error">{s.error}</div> : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={props.onClose} disabled={s.submitting}>
          Cancel
        </button>
        <button className="btn" disabled={s.submitting} onClick={props.onSubmit}>
          {s.submitting ? "Rotating…" : "Rotate"}
        </button>
      </div>
    </ModalShell>
  );
}

function RevokeModal(props: {
  state: RevokeState;
  onChange: (next: RevokeState) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const s = props.state;
  const label = s.key.label ?? "Untitled";
  return (
    <ModalShell>
      <h2 className="section-title">Revoke {label}?</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Anyone holding this key will stop being able to use it. This can't be undone.
      </p>
      <div className="field">
        <label className="label" htmlFor="revoke-reason">
          Reason (optional)
        </label>
        <input
          id="revoke-reason"
          className="input"
          autoFocus
          maxLength={REASON_MAX}
          value={s.reason}
          placeholder="e.g. Suspected leak"
          onChange={(e) => props.onChange({ ...s, reason: e.target.value })}
          disabled={s.submitting}
        />
        <span className="char-count">
          {s.reason.length}/{REASON_MAX}
        </span>
      </div>
      {s.error ? <div className="error">{s.error}</div> : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={props.onClose} disabled={s.submitting}>
          Cancel
        </button>
        <button className="btn btn-danger" disabled={s.submitting} onClick={props.onSubmit}>
          {s.submitting ? "Revoking…" : "Revoke"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: { children: ReactNode; warning?: boolean }) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className={props.warning ? "modal modal-warning" : "modal"}>{props.children}</div>
    </div>
  );
}

function KeyDisplay(props: { label: string; value: string; copied: boolean; onCopy: () => void }) {
  return (
    <div className="key-display">
      <div className="label">{props.label}</div>
      <div className="key-row">
        <code className="key-value">{props.value}</code>
        <button className="btn btn-secondary" onClick={props.onCopy}>
          {props.copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

// Relative time formatter. Returns "just now", "N minutes ago", "N hours
// ago", "N days ago", "N months ago", or "N years ago". For anything we
// can't parse, returns the raw value so the tooltip still shows the
// absolute timestamp.
function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const diff = Date.now() - parsed;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}
